import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageHeaderComponent } from '@/shared/page-header/page-header.component';
import { TableModule } from 'primeng/table';
import { DeviceContextService } from '../device-context.service';
import { BadgeModule } from 'primeng/badge';
import { PingLinkComponent } from '@/shared/ping-link/ping-link.component';
import { MessageModule } from 'primeng/message';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';

interface AutoDiscoveryDevice {
    hostname: string;
    model: string;
    ipAddress: string;
    macAddress: string;
    adapter: string;
    serialNumber: string;
    version: string;
    ipId: string;
}

/** Raw data structure from the backend payload */
interface AutoDiscoveryDeviceRaw {
    hostname?: string;
    model?: string;
    ipAddress?: string;
    macAddress?: string;
    adapter?: string;
    serialNumber?: string;
    version?: string;
    ipId?: string;
}

@Component({
    selector: 'app-cloud-autodiscovery',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        PageHeaderComponent,
        SectionCardComponent,
        EmptyStateComponent,
        TableModule,
        BadgeModule,
        MessageModule,
        PingLinkComponent
    ],
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                flex: 1;
                min-height: 0;
            }

            app-section-card {
                flex: 1;
            }

            /* The table scrolls inside the card, which fills the rest of the tab. */
            .results-container {
                flex: 1;
                display: flex;
                flex-direction: column;
                position: relative;
                overflow: auto;
            }

            p-table {
                position: absolute;
                min-width: 100%;
            }

            tr {
                white-space: nowrap;
            }

            tr.data {
                font-family: monospace;
            }

            .server-notice {
                margin-bottom: var(--app-page-gap);
            }
        `
    ],
    template: `
        <app-page-header title="Auto Discovery"></app-page-header>

        @if (isServerPlatform()) {
            <p-message class="server-notice" severity="info" icon="pi pi-info-circle">Auto Discovery is not yet available on server platforms. This feature is only available on appliance endpoints.</p-message>
        }

        <app-section-card title="Discovered Devices" subtitle="Crestron devices reported by the appliance during check-in" icon="fa fa-satellite-dish" [count]="hasResults() ? results().length : null" [flush]="true">
            <div class="results-container">
                @if (hasResults()) {
                    <p-table [value]="results()" [tableStyle]="{ 'min-width': '50rem' }" [rowHover]="true">
                        <ng-template #header>
                            <tr>
                                <th>Hostname</th>
                                <th>Model</th>
                                <th>IP Address</th>
                                <th>MAC Address</th>
                                <th>Adapter</th>
                                <th>Serial Number</th>
                                <th>Version</th>
                            </tr>
                        </ng-template>
                        <ng-template #body let-device>
                            <tr class="data">
                                <td>{{ device.hostname }}</td>
                                <td>{{ device.model }}</td>
                                <td>
                                    {{ device.ipAddress }}
                                    <app-ping-link [host]="device.ipAddress" />
                                </td>
                                <td>{{ device.macAddress }}</td>
                                <td>{{ device.adapter }}</td>
                                <td>{{ device.serialNumber }}</td>
                                <td>{{ device.version }}</td>
                            </tr>
                        </ng-template>
                    </p-table>
                } @else if (isServerPlatform()) {
                    <app-empty-state icon="pi pi-search" title="Auto Discovery is not yet supported on this platform" />
                } @else {
                    <app-empty-state icon="pi pi-search" title="No discovered devices available" description="Devices will appear here when reported by the appliance during check-in" />
                }
            </div>
        </app-section-card>
    `
})
export class CloudAutoDiscovery {
    private ctx = inject(DeviceContextService);
    deviceSig = this.ctx.device;

    results = computed<AutoDiscoveryDevice[]>(() => {
        const device = this.deviceSig();
        if (!device || !device.payload) return [];
        const autoDiscovery = device.payload.autoDiscovery;
        if (!Array.isArray(autoDiscovery)) return [];
        return autoDiscovery.map((d: AutoDiscoveryDeviceRaw) => ({
            hostname: d.hostname ?? '',
            model: d.model ?? '',
            ipAddress: d.ipAddress ?? '',
            macAddress: d.macAddress ?? '',
            adapter: d.adapter ?? '',
            serialNumber: d.serialNumber ?? '',
            version: d.version ?? '',
            ipId: d.ipId ?? ''
        }));
    });

    hasResults = computed(() => this.results().length > 0);

    isServerPlatform = computed(() => {
        const device = this.deviceSig();
        const platform = device?.payload?.devicePlatform;
        return platform === 'Server';
    });
}
